export class DeviceNotification {
  kind!: string
  associatedDevice?: string
  jsonData?: string
  rawData?: unknown

  getData<T = unknown>(): T | null {
    return decodeNotificationData<T>(this.rawData ?? this.jsonData)
  }

  getRecordData(): Record<string, unknown> | null {
    return asRecord(this.getData())
  }

  getNormalizedPayload(): DeviceNormalizedPayload | null {
    const raw = this.getRecordData()
    if (!raw) {
      return null
    }

    return normalizeDevicePayload(raw, this.associatedDevice)
  }
}

export interface DeviceNormalizedPayload {
  associatedDevice?: string
  status?: string
  orderGuid?: string
  orderNumber?: string
  mrgGuid?: string
  amount?: number
  currency?: string
  reasonCode?: string
  reasonMessage?: string
  transactionId?: string
  barcode?: string
  symbology?: string
  raw: Record<string, unknown>
}

export type DeviceNotificationListener = (notification: DeviceNotification) => void
export type DeviceOperationListener = (type: string, orderGuid: string, notification: DeviceNotification) => void

export class PeripheralBase {
  name = ''
  guid = ''
  type = ''
  state: string | null = null

  isAvailable(): boolean {
    if (this.state == null) {
      return true
    }

    switch (this.state.toLowerCase()) {
      case 'available':
      case 'active':
        return true
      default:
        return false
    }
  }

  IsAvailable(): boolean {
    return this.isAvailable()
  }
}

export abstract class PeripheralBarCode extends PeripheralBase {
  protected globalListener?: DeviceNotificationListener
  protected successListener?: DeviceOperationListener
  protected failureListener?: DeviceOperationListener

  on(callback: DeviceNotificationListener): void {
    this.globalListener = callback
  }

  onSuccess(callback: DeviceOperationListener): void {
    this.successListener = callback
  }

  onError(callback: DeviceOperationListener): void {
    this.failureListener = callback
  }

  dispatchNotification(notification: DeviceNotification): void {
    this.globalListener?.(notification)
  }

  dispatchSuccess(type: string, orderGuid: string, notification: DeviceNotification): void {
    this.successListener?.(type, orderGuid, notification)
  }

  dispatchFailure(type: string, orderGuid: string, notification: DeviceNotification): void {
    this.failureListener?.(type, orderGuid, notification)
  }
}

export abstract class PeripheralTpe extends PeripheralBase {
  protected globalListener?: DeviceNotificationListener
  protected successListener?: DeviceOperationListener
  protected failureListener?: DeviceOperationListener

  on(callback: DeviceNotificationListener): void {
    this.globalListener = callback
  }

  onSuccess(callback: DeviceOperationListener): void {
    this.successListener = callback
  }

  onFailure(callback: DeviceOperationListener): void {
    this.failureListener = callback
  }

  onError(callback: DeviceOperationListener): void {
    this.failureListener = callback
  }

  dispatchNotification(notification: DeviceNotification): void {
    this.globalListener?.(notification)
  }

  dispatchSuccess(type: string, orderGuid: string, notification: DeviceNotification): void {
    this.successListener?.(type, orderGuid, notification)
  }

  dispatchFailure(type: string, orderGuid: string, notification: DeviceNotification): void {
    this.failureListener?.(type, orderGuid, notification)
  }

  abstract makePayment(orderGuid: string, orderNumber: string, mrgGuid: string, amount: number, currency: string): void
  abstract makeRefund(orderGuid: string, orderNumber: string, mrgGuid: string, amount: number, currency: string): void
  abstract cancelPendingPayment(): void
}

interface WebViewMessageEvent {
  data: unknown
}

interface WebViewHost {
  postMessage(message: unknown): void
  addEventListener(type: 'message', listener: (event: WebViewMessageEvent) => void): void
  removeEventListener(type: 'message', listener: (event: WebViewMessageEvent) => void): void
}

interface HostDeviceDescriptor {
  guid?: string
  name?: string
  type?: string
  state?: string
  status?: string
  deviceGuid?: string
  deviceName?: string
  label?: string
  kind?: string
  deviceKind?: string
}

interface WindowWithWebView extends Window {
  chrome?: {
    webview?: WebViewHost
  }
}

export class DeviceManager {
  private initialized = false
  private globalListener?: DeviceNotificationListener
  private peripherals: PeripheralBase[] = []
  private readonly host: WebViewHost | null
  private readonly hostListener = (event: WebViewMessageEvent): void => {
    const notification = this.normalizeNotification(event.data)
    if (notification) {
      this.notify(notification)
    }
  }

  constructor() {
    this.host = this.resolveHost()
    this.host?.addEventListener('message', this.hostListener)
  }

  get isHostAvailable(): boolean {
    return this.host !== null
  }

  registerGlobalCallback(callback: DeviceNotificationListener): void {
    if (!this.initialized) {
      this.initialize()
    }

    this.globalListener = callback
  }

  initialize(mockMode = false): void {
    if (!mockMode && !this.isHostAvailable) {
      return
    }

    this.initialized = true
    this.peripherals = []

    if (mockMode) {
      const mockTpe = new DeviceTpeMock((notification) => this.notify(notification))
      mockTpe.guid = '86602b7f-3d17-4715-9b5f-7109f4fe92f4'
      mockTpe.name = 'MockTPE'
      mockTpe.type = 'TPE'
      this.peripherals.push(mockTpe)
      return
    }

    this.host?.postMessage({
      kind: 'DeviceManager.Init',
    })
  }

  init(mockMode = false): void {
    this.initialize(mockMode)
  }

  dispose(): void {
    this.host?.removeEventListener('message', this.hostListener)
  }

  findPeripheral(guid: string): PeripheralBase | null {
    if (!this.initialized) {
      this.initialize()
    }

    for (const peripheral of this.peripherals) {
      if (peripheral.guid === guid) {
        return peripheral
      }
    }

    return null
  }

  findPeripheralByType(type: string): PeripheralBase[] {
    if (!this.initialized) {
      this.initialize()
    }

    return this.peripherals.filter((peripheral) => peripheral.type.toLowerCase() === type.toLowerCase())
  }

  findPeripheralsByType(type: string): PeripheralBase[] {
    return this.findPeripheralByType(type)
  }

  notify(notification: DeviceNotification): void {
    if (notification.kind.trim().length === 0) {
      return
    }

    const kind = notification.kind.toLowerCase()

    switch (kind) {
      case 'status_update': {
        const device = notification.associatedDevice ? this.findPeripheral(notification.associatedDevice) : null
        const status = this.readStatus(notification)
        if (device && status) {
          device.state = status
        }
        this.globalListener?.(notification)
        break
      }

      case 'init': {
        this.initialized = true
        this.peripherals = this.createPeripherals(resolveDeviceDescriptors(notification.getData()))
        this.globalListener?.(notification)
        return
      }

      default:
        this.globalListener?.(notification)
        break
    }

    if (kind.startsWith('tpe:') || kind.startsWith('barcode:')) {
      this.dispatchPeripheralNotification(notification, kind)
    }
  }

  changeShellState(newState: string): void {
    this.host?.postMessage({
      kind: 'shell.state-change',
      data: {
        desiredState: newState,
      },
    })
  }

  sendAppStatusUpdate(currentApp: string, currentStatus: string, value: string | null = null): void {
    this.host?.postMessage({
      kind: 'app.status-update',
      data: {
        app: currentApp,
        status: currentStatus,
        value,
      },
    })
  }

  private resolveHost(): WebViewHost | null {
    if (typeof window === 'undefined') {
      return null
    }

    return (window as WindowWithWebView).chrome?.webview ?? null
  }

  private normalizeNotification(data: unknown): DeviceNotification | null {
    if (data instanceof DeviceNotification) {
      if (data.rawData === undefined && data.jsonData !== undefined) {
        data.rawData = data.jsonData
      }
      return data
    }

    const candidate = resolveNotificationRecord(data)
    if (!candidate) {
      return null
    }

    const kind = normalizeNotificationKind(readStringValue(candidate, ['kind', 'Kind', 'eventKind', 'notificationKind', 'type', 'Type']))
    if (!kind) {
      return null
    }

    const notification = new DeviceNotification()
    const payload = resolveNotificationPayload(candidate)
    notification.kind = kind
    notification.associatedDevice = readStringValue(candidate, [
      'associatedDevice',
      'AssociatedDevice',
      'associatedDeviceGuid',
      'AssociatedDeviceGuid',
      'deviceGuid',
      'DeviceGuid',
      'deviceId',
      'DeviceId',
      'peripheralGuid',
      'PeripheralGuid',
    ]) ?? readAssociatedDeviceValue(payload) ?? undefined
    notification.rawData = payload
    notification.jsonData = encodeNotificationData(payload)
    return notification
  }

  private createPeripherals(devices: HostDeviceDescriptor[]): PeripheralBase[] {
    const peripherals: PeripheralBase[] = []

    for (const device of devices) {
      const peripheral = this.createPeripheral(device)
      if (peripheral) {
        peripherals.push(peripheral)
      }
    }

    return peripherals
  }

  private createPeripheral(device: HostDeviceDescriptor): PeripheralBase | null {
    const type = readStringValue(device, ['type', 'kind', 'deviceKind'])?.toLowerCase()
    let peripheral: PeripheralBase | null

    switch (type) {
      case 'tpe':
        peripheral = new DeviceTpeShell(
          (notification) => this.notify(notification),
          (message) => this.host?.postMessage(message),
        )
        break
      case 'barcodescanner':
      case 'barcode':
        peripheral = new DeviceBarCodeShell()
        break
      default:
        peripheral = new PeripheralBase()
        break
    }

    peripheral.guid = readStringValue(device, ['guid', 'deviceGuid', 'id']) ?? ''
    peripheral.name = readStringValue(device, ['name', 'deviceName', 'label']) ?? ''
    peripheral.type = readStringValue(device, ['type', 'kind', 'deviceKind']) ?? ''
    peripheral.state = readStringValue(device, ['state', 'status']) ?? null
    return peripheral
  }

  private readStatus(notification: DeviceNotification): string | null {
    return notification.getNormalizedPayload()?.status ?? null
  }

  private dispatchPeripheralNotification(notification: DeviceNotification, kind: string): void {
    if (!notification.associatedDevice) {
      return
    }

    const device = this.findPeripheral(notification.associatedDevice)
    if (!device) {
      return
    }

    const orderGuid = notification.getNormalizedPayload()?.orderGuid ?? ''

    if (device instanceof PeripheralTpe) {
      switch (kind) {
        case 'tpe:payment-success':
          device.dispatchSuccess('payment', orderGuid, notification)
          break
        case 'tpe:payment-failure':
          device.dispatchFailure('payment', orderGuid, notification)
          break
        case 'tpe:refund-success':
          device.dispatchSuccess('refund', orderGuid, notification)
          break
        case 'tpe:refund-failure':
          device.dispatchFailure('refund', orderGuid, notification)
          break
        default:
          break
      }

      device.dispatchNotification(notification)
      return
    }

    if (device instanceof PeripheralBarCode) {
      switch (kind) {
        case 'barcode:scan':
          device.dispatchSuccess('scan', orderGuid, notification)
          break
        case 'barcode:error':
          device.dispatchFailure('scan', orderGuid, notification)
          break
        default:
          break
      }

      device.dispatchNotification(notification)
    }
  }
}

function decodeNotificationData<T>(value: unknown): T | null {
  const decoded = unwrapSerializedData(value)
  return decoded == null ? null : decoded as T
}

function unwrapSerializedData(value: unknown): unknown {
  let current = value

  for (let index = 0; index < 3; index += 1) {
    if (current == null) {
      return null
    }

    if (typeof current !== 'string') {
      return current
    }

    const trimmed = current.trim()
    if (trimmed.length === 0) {
      return null
    }

    try {
      current = JSON.parse(trimmed)
      continue
    } catch {
      return current
    }
  }

  return current
}

function encodeNotificationData(value: unknown): string | undefined {
  if (value == null) {
    return undefined
  }

  if (typeof value === 'string') {
    return value
  }

  try {
    return JSON.stringify(value)
  } catch {
    return undefined
  }
}

function resolveNotificationRecord(data: unknown): Record<string, unknown> | null {
  let current = asRecord(data)

  for (let depth = 0; depth < 4 && current; depth += 1) {
    if (readStringValue(current, ['kind', 'Kind', 'eventKind', 'notificationKind', 'type', 'Type'])) {
      return current
    }

    const nested = firstRecord(current, ['message', 'notification', 'event', 'payload', 'data', 'Data'])
    if (!nested) {
      return null
    }

    current = nested
  }

  return null
}

function resolveNotificationPayload(notification: Record<string, unknown>): unknown {
  for (const key of ['jsonData', 'JsonData', 'payload', 'Payload', 'json', 'Json', 'data', 'Data']) {
    if (!(key in notification)) {
      continue
    }

    const value = notification[key]
    if (typeof value === 'string' || Array.isArray(value)) {
      return value
    }

    const nested = asRecord(value)
    if (nested && !readStringValue(nested, ['kind', 'Kind', 'eventKind', 'notificationKind', 'type', 'Type'])) {
      return value
    }
  }

  return undefined
}

function normalizeNotificationKind(value: string | null): string | null {
  if (!value) {
    return null
  }

  const normalized = value.trim().toLowerCase().replace(/\s+/g, '')
  const unified = normalized.replace(/[.\/]+/g, ':').replace(/_/g, '-')

  switch (unified) {
    case 'init':
    case 'devicemanager:init':
    case 'device-manager:init':
      return 'init'
    case 'status-update':
    case 'statusupdate':
      return 'status_update'
    default:
      if (unified.startsWith('tpe-')) {
        return `tpe:${unified.slice('tpe-'.length)}`
      }

      if (unified.startsWith('barcode-')) {
        return `barcode:${unified.slice('barcode-'.length)}`
      }

      return unified
  }
}

function firstRecord(source: Record<string, unknown>, keys: string[]): Record<string, unknown> | null {
  for (const key of keys) {
    const value = source[key]
    const record = asRecord(value)
    if (record) {
      return record
    }
  }

  return null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function readStringValue(source: object, keys: string[]): string | null {
  const record = source as Record<string, unknown>

  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed.length > 0) {
        return trimmed
      }
    }
  }

  return null
}

function resolveDeviceDescriptors(payload: unknown): HostDeviceDescriptor[] {
  if (Array.isArray(payload)) {
    return payload.filter((entry): entry is HostDeviceDescriptor => asRecord(entry) !== null)
  }

  const record = asRecord(payload)
  if (!record) {
    return []
  }

  for (const key of ['devices', 'Devices', 'peripherals', 'Peripherals', 'data', 'Data']) {
    const value = record[key]
    if (Array.isArray(value)) {
      return value.filter((entry): entry is HostDeviceDescriptor => asRecord(entry) !== null)
    }

    const nested = asRecord(value)
    if (nested) {
      const nestedDevices = resolveDeviceDescriptors(nested)
      if (nestedDevices.length > 0) {
        return nestedDevices
      }
    }
  }

  return []
}

function readAssociatedDeviceValue(payload: unknown): string | null {
  const record = asRecord(payload)
  if (!record) {
    return null
  }

  const direct = readStringValue(record, [
    'associatedDevice',
    'AssociatedDevice',
    'associatedDeviceGuid',
    'AssociatedDeviceGuid',
    'deviceGuid',
    'DeviceGuid',
    'deviceId',
    'DeviceId',
    'peripheralGuid',
    'PeripheralGuid',
  ])
  if (direct) {
    return direct
  }

  for (const key of ['data', 'Data', 'payload', 'Payload']) {
    const nested = asRecord(record[key])
    if (!nested) {
      continue
    }

    const nestedDevice = readAssociatedDeviceValue(nested)
    if (nestedDevice) {
      return nestedDevice
    }
  }

  return null
}

function normalizeDevicePayload(
  record: Record<string, unknown>,
  associatedDevice?: string,
): DeviceNormalizedPayload {
  return {
    associatedDevice: associatedDevice ?? readAssociatedDeviceValue(record) ?? undefined,
    status: readStringValueDeep(record, ['status', 'Status', 'state', 'State']) ?? undefined,
    orderGuid: readStringValueDeep(record, ['orderGuid', 'OrderGuid', 'orderGUID', 'orderId', 'OrderId']) ?? undefined,
    orderNumber: readStringValueDeep(record, ['orderNumber', 'OrderNumber', 'reference', 'Reference']) ?? undefined,
    mrgGuid: readStringValueDeep(record, ['mrgGuid', 'MrgGuid', 'mrgId', 'MrgId']) ?? undefined,
    amount: readNumberValueDeep(record, ['amount', 'Amount', 'totalAmount', 'TotalAmount']) ?? undefined,
    currency: readStringValueDeep(record, ['currency', 'Currency', 'currencyCode', 'CurrencyCode']) ?? undefined,
    reasonCode: readStringValueDeep(record, ['reasonCode', 'ReasonCode', 'code', 'Code']) ?? undefined,
    reasonMessage: readStringValueDeep(record, ['reasonMessage', 'ReasonMessage', 'message', 'Message']) ?? undefined,
    transactionId: readStringValueDeep(record, ['transactionId', 'TransactionId', 'transactionNumber', 'TransactionNumber']) ?? undefined,
    barcode: readStringValueDeep(record, ['barcode', 'Barcode', 'value', 'Value', 'scanValue', 'ScanValue']) ?? undefined,
    symbology: readStringValueDeep(record, ['symbology', 'Symbology', 'barcodeType', 'BarcodeType']) ?? undefined,
    raw: record,
  }
}

function readStringValueDeep(source: Record<string, unknown>, keys: string[]): string | null {
  const direct = readStringValue(source, keys)
  if (direct) {
    return direct
  }

  for (const key of ['data', 'Data', 'payload', 'Payload', 'result', 'Result']) {
    const nested = asRecord(source[key])
    if (!nested) {
      continue
    }

    const nestedValue = readStringValueDeep(nested, keys)
    if (nestedValue) {
      return nestedValue
    }
  }

  return null
}

function readNumberValueDeep(source: Record<string, unknown>, keys: string[]): number | null {
  const direct = readNumberValue(source, keys)
  if (direct !== null) {
    return direct
  }

  for (const key of ['data', 'Data', 'payload', 'Payload', 'result', 'Result']) {
    const nested = asRecord(source[key])
    if (!nested) {
      continue
    }

    const nestedValue = readNumberValueDeep(nested, keys)
    if (nestedValue !== null) {
      return nestedValue
    }
  }

  return null
}

function readNumberValue(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string') {
      const parsed = Number(value.trim())
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

function readOrderGuid(payload: unknown): string {
  const record = asRecord(payload)
  if (!record) {
    return ''
  }

  return normalizeDevicePayload(record).orderGuid ?? ''
}

class DeviceTpeMock extends PeripheralTpe {
  private cancelled = false

  constructor(private readonly emitNotification: (notification: DeviceNotification) => void) {
    super()
  }

  makePayment(orderGuid: string, orderNumber: string, mrgGuid: string, amount: number, currency: string): void {
    this.cancelled = false

    this.schedule(200, () => this.createNotification('TPE:Progress', {
      orderGuid,
      orderNumber,
      mrgGuid,
      amount,
      currency,
      reasonCode: 'START',
      reasonMessage: 'Veuillez suivre les instructions sur le terminal de paiement',
    }))

    this.schedule(2500, () => this.createNotification('TPE:Progress', {
      orderGuid,
      orderNumber,
      mrgGuid,
      amount,
      currency,
      reasonCode: 'PINCODE',
      reasonMessage: 'Saisie du pin-code en attente',
    }))

    if (orderNumber.startsWith('F')) {
      this.schedule(7500, () => this.createNotification('TPE:Payment-Failure', {
        orderGuid,
        orderNumber,
        mrgGuid,
        amount,
        currency,
        reasonCode: 'FAKE',
        reasonMessage: "Votre paiement n'a pas pu être traité",
      }))
      return
    }

    this.schedule(7500, () => this.createNotification('TPE:Payment-Success', {
      orderGuid,
      orderNumber,
      mrgGuid,
      amount,
      currency,
      transactionId: 'FakeTransactionNumber',
      reasonCode: 'FAKE',
      reasonMessage: 'Paiement terminé',
    }))
  }

  makeRefund(orderGuid: string, orderNumber: string, mrgGuid: string, amount: number, currency: string): void {
    this.cancelled = false
    this.schedule(2500, () => this.createNotification('TPE:Refund-Failure', {
      orderGuid,
      orderNumber,
      mrgGuid,
      amount,
      currency,
      reasonCode: 'FAKE',
      reasonMessage: "Votre paiement n'a pas pu être remboursé : nous sommes en mode mock",
    }))
  }

  cancelPendingPayment(): void {
    this.cancelled = true

    this.schedule(2500, () => this.createNotification('TPE:Payment-Failure', {
      reasonCode: 'CANCEL',
      reasonMessage: 'Paiement annulé',
    }))
  }

  private schedule(delay: number, factory: () => DeviceNotification): void {
    setTimeout(() => {
      if (!this.cancelled) {
        this.emitNotification(factory())
      }
    }, delay)
  }

  private createNotification(kind: string, payload: Record<string, unknown>): DeviceNotification {
    const notification = new DeviceNotification()
    notification.kind = kind
    notification.associatedDevice = this.guid
    notification.jsonData = JSON.stringify(payload)
    return notification
  }
}

class DeviceTpeShell extends PeripheralTpe {
  constructor(
    private readonly emitNotification: (notification: DeviceNotification) => void,
    private readonly postMessage: (message: unknown) => void,
  ) {
    super()
  }

  setState(state: string): void {
    const notification = new DeviceNotification()
    notification.kind = 'STATUS_UPDATE'
    notification.associatedDevice = this.guid
    notification.jsonData = JSON.stringify({ status: state })
    this.emitNotification(notification)
  }

  makePayment(orderGuid: string, orderNumber: string, mrgGuid: string, amount: number, currency: string): void {
    this.postMessage({
      deviceKind: 'TPE',
      deviceGuid: this.guid,
      kind: 'payment-request',
      data: {
        orderGuid,
        orderNumber,
        mrgGuid,
        amount,
        currency,
      },
    })
  }

  makeRefund(orderGuid: string, orderNumber: string, mrgGuid: string, amount: number, currency: string): void {
    this.postMessage({
      deviceKind: 'TPE',
      deviceGuid: this.guid,
      kind: 'refund-request',
      data: {
        orderGuid,
        orderNumber,
        mrgGuid,
        amount,
        currency,
      },
    })
  }

  cancelPendingPayment(): void {
    this.postMessage({
      deviceKind: 'TPE',
      deviceGuid: this.guid,
      kind: 'payment-request-cancel',
      data: {},
    })
  }
}

class DeviceBarCodeShell extends PeripheralBarCode {}